/* eslint-disable no-console */
import axios, { AxiosError } from 'axios';
import { ANALYTICS_API_BASE_URL } from '../env';
import { toast } from 'react-toastify';
import { routesPath } from '../constants/routes';
import { lastUrlStorage } from '../utilities/helpers';

const callToast = (
  error: AxiosError<{ code?: string; message?: string; extra?: Record<string, unknown> }>,
) => {
  const status = error.response?.status;
  const body = error.response?.data;
  const code = body?.code;

  // Handle ForceResetPassword flow
  if (code === 'ForceResetPassword' && body?.extra?.resetToken) {
    localStorage.setItem('resetToken', body.extra.resetToken as string);
    window.location.href = routesPath.resetPassword;
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return new Promise(() => {});
  }

  // Handle 401 Unauthorized
  if (status === 401) {
    const token = localStorage.getItem('token');
    if (token && token !== 'null') {
      localStorage.removeItem('token');
      lastUrlStorage.save();
      toast.error(body?.message ?? 'Not authorized', {
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      window.location.href = routesPath.login;
    }
    return Promise.reject(error);
  }

  // Don't toast 422 validation errors
  if (status === 422) {
    return Promise.reject(error);
  }

  // Handle other errors
  if (error.response) {
    let message = body?.message || 'Error';
    if (/doctype/.test(message)) {
      message = 'Oops! Something went wrong!';
    }
    toast.error(message, {
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
    });
  } else if (error.request) {
    console.error('Network error:', error.request);
    toast.error('Network error. Please check your connection.', {
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
    });
  } else {
    console.error('Error:', error.message);
    toast.error(error.message || 'Error', {
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
    });
  }

  return Promise.reject(error);
};

export const RESTClient = axios.create({
  baseURL: ANALYTICS_API_BASE_URL + '/api/v1',

  transformResponse: [
    (data) => {
      if (typeof data === 'string') {
        return JSON.parse(data.replace(/(-?)Infinity|NaN/g, 'null'));
      }
      return data;
    },
  ],
});

RESTClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.method === 'get') {
    config.params = {
      ...config.params,
      source: 'sql',
    };
  }
  return config;
});

RESTClient.interceptors.response.use((response) => response, callToast);
